import React, { useState, useMemo } from 'react';
import { NavLink, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/config";
import { usePermissions } from "../hooks/usePermissions";

// --- WIDGETS LATERALES ---
import WidgetObjetivosSancor from './Widgets/WidgetObjetivosSancor';
import WidgetProduccionMensual from './Widgets/WidgetProduccionMensual';

// --- CONFIGURACIÓN DE SECCIONES Y MÓDULOS ---
const SECTIONS_CONFIG = [
    {
        id: "busqueda",
        header: "Búsqueda & Cartera",
        sub: "Herramientas de consulta rápida y administración de clientes",
        badgeColor: "bg-indigo-100 text-indigo-700 border-indigo-200",
        modules: [
            {
                title: "Buscador Global",
                path: "/BuscadorPolizasPage",
                category: "Consultas",
                icon: (
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                ),
                description: "Pólizas por cliente, DNI, patente o número de contrato.",
                chipBg: "bg-indigo-50 text-indigo-700 border-indigo-100",
                hoverBorder: "hover:border-indigo-300"
            },
            {
                title: "Administrador de Clientes",
                path: "/clientes",
                category: "Asegurados",
                icon: (
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                ),
                description: "Cartera unificada, perfiles y estados de cuenta.",
                chipBg: "bg-blue-50 text-blue-700 border-blue-100",
                hoverBorder: "hover:border-blue-300"
            }
        ]
    },
    {
        id: "operaciones",
        header: "Operaciones & Carga",
        sub: "Nuevas emisiones, solicitudes y denuncias de siniestros",
        badgeColor: "bg-emerald-100 text-emerald-700 border-emerald-200",
        modules: [
            {
                title: "Cargar Emisión",
                path: "/CargaPolizaPage",
                category: "Nuevas Pólizas",
                icon: (
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                ),
                description: "Alta rápida de nuevas pólizas patrimoniales.",
                chipBg: "bg-emerald-50 text-emerald-700 border-emerald-100",
                hoverBorder: "hover:border-emerald-300"
            },
            {
                title: "Cotizar ART",
                path: "/CargaCotizacionArtPage",
                category: "Comercial",
                icon: (
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                ),
                description: "Solicitud y negociación de Riesgos del Trabajo.",
                chipBg: "bg-amber-50 text-amber-700 border-amber-100",
                hoverBorder: "hover:border-amber-300"
            },
            {
                title: "Cargar Endoso",
                path: "/CargaEndosoPage",
                category: "Modificaciones",
                icon: (
                    <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z" />
                    </svg>
                ),
                description: "Modificaciones operativas, refacturaciones y bajas.",
                chipBg: "bg-cyan-50 text-cyan-700 border-cyan-100",
                hoverBorder: "hover:border-cyan-300"
            },
            {
                title: "Cargar Siniestro",
                path: "/CargaSiniestroPage",
                category: "Siniestros",
                icon: (
                    <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                ),
                description: "Registro de denuncias y reclamos de terceros.",
                chipBg: "bg-rose-50 text-rose-700 border-rose-100",
                hoverBorder: "hover:border-rose-300"
            }
        ]
    },
    {
        id: "tableros",
        header: "Gestión & Tableros",
        sub: "Monitoreo de vencimientos, cobranzas y estado de vigencias",
        badgeColor: "bg-blue-100 text-blue-700 border-blue-200",
        modules: [
            {
                title: "Renovaciones",
                path: "/Renovaciones",
                category: "60 Días",
                icon: (
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                ),
                description: "Pólizas próximas a vencer.",
                chipBg: "bg-blue-50 text-blue-700 border-blue-100",
                hoverBorder: "hover:border-blue-300"
            },
            {
                title: "Refacturación de Pólizas",
                path: "/RefacturacionPolizas",
                category: "30 Días",
                icon: (
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                ),
                description: "Refacturaciones periódicas programadas.",
                chipBg: "bg-amber-50 text-amber-700 border-amber-100",
                hoverBorder: "hover:border-amber-300"
            },
            {
                title: "Gestión de Cobranza",
                path: "/Cobranza",
                category: "Finanzas",
                icon: (
                    <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                ),
                description: "Control de cuotas e impagos de cartera.",
                chipBg: "bg-rose-50 text-rose-700 border-rose-100",
                hoverBorder: "hover:border-rose-300"
            },
            {
                title: "Gestión de Cauciones",
                path: "/refacturaciones",
                category: "Garantías",
                icon: (
                    <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                ),
                description: "Renovación y cobro de cauciones.",
                chipBg: "bg-violet-50 text-violet-700 border-violet-100",
                hoverBorder: "hover:border-violet-300"
            },
            {
                title: "Pólizas Pendientes",
                path: "/PolizasPendientes",
                category: "Trámites",
                icon: (
                    <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                ),
                description: "Pólizas y contratos a la espera de emisión.",
                chipBg: "bg-cyan-50 text-cyan-700 border-cyan-100",
                hoverBorder: "hover:border-cyan-300"
            },
            {
                title: "Gestión de Endosos",
                path: "/GestionEndosos",
                category: "Trámites",
                icon: (
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                ),
                description: "Seguimiento y control de endosos cargados.",
                chipBg: "bg-purple-50 text-purple-700 border-purple-100",
                hoverBorder: "hover:border-purple-300"
            },
            {
                title: "Gestión de Siniestros",
                path: "/GestionSiniestros",
                category: "Atención",
                icon: (
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                ),
                description: "Control y avance de siniestros denunciados.",
                chipBg: "bg-emerald-50 text-emerald-700 border-emerald-100",
                hoverBorder: "hover:border-emerald-300"
            },
            {
                title: "Pólizas Periodo Corto",
                path: "/GestionPeriodoCorto",
                category: "30 días",
                icon: (
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                ),
                description: "Pólizas de periodo corto próximas a vencer",
                chipBg: "bg-amber-50 text-amber-700 border-amber-100",
                hoverBorder: "hover:border-amber-300"
            },
            {
                title: "Gestión Post-Venta",
                path: "/GestionPostVenta",
                category: "Seguimiento",
                icon: (
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                ),
                description: "Gestión Post venta de Pólizas",
                chipBg: "bg-purple-50 text-purple-700 border-purple-100",
                hoverBorder: "hover:border-purple-300"
            },
            {
                title: "Cobranza ART",
                path: "#",
                isDisabled: true, // ◄ Módulo inactivo / Próximamente
                category: "Próximamente",
                icon: (
                    <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                ),
                description: "Auditoría de saldo y cobros de ART.",
                chipBg: "bg-slate-100 text-slate-500 border-slate-200",
                hoverBorder: "hover:border-slate-200"
            }
        ]
    },
    {
        id: "prospectos",
        header: "Prospectos",
        description: "Seguimiento de oportunidades comerciales previas a la emisión.",
        accent: "border-sky-500",
        modules: [
            {
                title: "Autos",
                path: "/ProspectosAutos",
                category: "Prospectos",
                icon: (
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                    </svg>
                ),
                description: "Prospectos de seguros de automotores.",
                color: "text-sky-600",
                bg: "bg-sky-50 group-hover:bg-sky-600 group-hover:text-white"
            },
            {
                title: "Clientes Croselling",
                path: "/ClientesCroselling",
                category: "Oportunudades de Venta",
                icon: (
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                ),
                description: "cliente calificados para croselling",
                chipBg: "bg-purple-50 text-purple-700 border-purple-100",
                hoverBorder: "hover:border-purple-300"
            },
        ]
    },
    {
        id: "config",
        header: "Administración & Datos",
        sub: "Ajustes globales, acuerdos y carga masiva de datos",
        badgeColor: "bg-purple-100 text-purple-700 border-purple-200",
        modules: [
            {
                title: "Panel de Administración",
                path: "/AdminDashboard",
                category: "Ajustes",
                icon: (
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                ),
                description: "Reglas, permisos, usuarios y comisiones.",
                chipBg: "bg-purple-50 text-purple-700 border-purple-100",
                hoverBorder: "hover:border-purple-300"
            },
            {
                title: "Importar Deuda Patrimonial",
                path: "/ImportarDeudaPage",
                category: "Archivos",
                icon: (
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                ),
                description: "Procesamiento masivo de extractos externos.",
                chipBg: "bg-amber-50 text-amber-700 border-amber-100",
                hoverBorder: "hover:border-amber-300"
            },
            {
                title: "Importar Deuda ART",
                path: "/ImportarDeudasArtPage",
                category: "Archivos",
                icon: (
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                ),
                description: "Carga masiva de liquidaciones de ART.",
                chipBg: "bg-amber-50 text-amber-700 border-amber-100",
                hoverBorder: "hover:border-amber-300"
            },
            {
                title: "Reportes Avanzados",
                path: "/ReportesHub",
                category: "Analítica",
                icon: (
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                ),
                description: "Reportes masivos y métricas globales.",
                chipBg: "bg-indigo-50 text-indigo-700 border-indigo-100",
                hoverBorder: "hover:border-indigo-300"
            }
        ]
    }
];

const Cpanel = () => {
    const navigate = useNavigate();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    
    // Estado de colapso de acordeones
    const [collapsedSections, setCollapsedSections] = useState({});

    // ◄◄◄ CONSUMO DEL HOOK CON LAS PROPIEDADES DE USUARIO ◄◄◄
    const { can, isFullUser, loadingAuth, userName, userInitials, userEmail } = usePermissions();

    const toggleSection = (id) => {
        setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleLogout = async () => {
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        try {
            await signOut(auth);
            navigate("/");
        } catch (error) {
            console.error("Error al revocar la sesión activa:", error);
            setIsLoggingOut(false);
        }
    };

    const filteredSections = useMemo(() => {
        return SECTIONS_CONFIG.map(section => {
            const filteredModules = section.modules.filter(module => {
                if (!isFullUser) {
                    return ["/BuscadorPolizasPage", "/CargaCotizacionArtPage"].includes(module.path);
                }

                if (module.path === "/AdminDashboard") return can('VER_PANEL_ADMIN');
                if (module.path === "/ImportarDeudaPage") return can('VER_IMPORTADOR_DEUDA');
                if (module.path === "/ReportesHub") return can('VER_REPORTES_AVANZADOS');
                
                return true;
            });

            return { ...section, modules: filteredModules };
        }).filter(section => section.modules.length > 0);
    }, [can, isFullUser]);

    if (loadingAuth) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center font-sans select-none">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
                    <span className="text-slate-500 text-xs font-bold tracking-wider animate-pulse">Cargando Consola IB Brokers...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f3f4f8] font-sans text-left pb-16 w-full antialiased">
            
            {/* HEADER SUPERIOR CON LOGO LIMPIO, USUARIO, NOTIFICACIONES Y CERRAR SESIÓN */}
            <header className="bg-white border-b border-slate-200/80 shadow-xs px-8 py-4 sticky top-0 z-30">
                <div className="w-full flex items-center justify-between gap-4">
                    
                    {/* ISOLOGO GRANDE Y LIMPIO + TÍTULO */}
                    <div className="flex items-center gap-20">
                        <img 
                            src="/imagenes/Logo.svg" 
                            alt="Logo IB" 
                            className="h-12 w-auto object-contain shrink-0" 
                        />
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-lg font-black text-slate-900 tracking-tight leading-none">
                                    Workspace
                                </h1>
                                <span className="bg-emerald-50 text-emerald-700 text-[10px] font-black px-2.5 py-0.5 rounded-full border border-emerald-200 uppercase tracking-wider flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                    En Línea
                                </span>
                            </div>
                            <span className="text-[11px] text-slate-400 font-semibold tracking-wide block mt-1">
                                Sistema de Gestión e Inteligencia Comercial
                            </span>
                        </div>
                    </div>

                    {/* ACCIONES SUPERIORES: USUARIO CON INICIALES + CAMPANITA + BOTÓN CERRAR SESIÓN */}
                    <div className="flex items-center gap-5">
                        
                        {/* ◄◄◄ TARJETA DE USUARIO LOGUEADO CON INICIALES REALES ◄◄◄ */}
                        <div className="flex items-center gap-3 bg-slate-50 p-1.5 pr-4 rounded-2xl border border-slate-200/80 shadow-2xs">
                            <div className="w-9 h-9 rounded-xl bg-slate-900 text-white font-black text-xs flex items-center justify-center shrink-0 font-mono shadow-xs border border-slate-800">
                                {userInitials}
                            </div>
                            <div className="text-left">
                                <span className="text-xs font-black text-slate-800 block leading-tight uppercase truncate max-w-[160px]">
                                    {userName}
                                </span>
                                <span className="text-[10px] font-bold text-slate-400 block truncate max-w-[160px]">
                                    {userEmail}
                                </span>
                            </div>
                        </div>

                        {/* CAMPANITA DE NOTIFICACIONES */}
                        <div className="relative group flex items-center">
                            <button 
                                type="button" 
                                className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:text-blue-600 hover:bg-slate-50 transition-all cursor-pointer relative bg-white shadow-2xs"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                
                                <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full">
                                    <span className="absolute inset-0 rounded-full bg-rose-400 animate-ping opacity-75"></span>
                                </span>
                            </button>

                            <div className="absolute right-0 top-12 opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-y-1 group-hover:translate-y-0 pointer-events-none z-50">
                                <div className="bg-slate-900 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl shadow-xl flex items-center gap-1.5 whitespace-nowrap relative border border-slate-700">
                                    <span>Próximamente</span>
                                    <div className="absolute -top-1 right-3.5 w-2 h-2 bg-slate-900 rotate-45 border-t border-l border-slate-700"></div>
                                </div>
                            </div>
                        </div>

                        {/* BOTÓN CERRAR SESIÓN */}
                        <button 
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-rose-200/90 text-rose-600 font-bold hover:bg-rose-50 hover:border-rose-300 transition-all text-xs bg-white shadow-2xs cursor-pointer disabled:opacity-50"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span>{isLoggingOut ? 'Cerrando...' : 'Cerrar Sesión'}</span>
                        </button>

                    </div>

                </div>
            </header>

            {/* CONTENEDOR 100% ANCHO (GRID DUAL: 3/4 MÓDULOS + 1/4 WIDGETS) */}
            <div className="w-full px-8 pt-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    
                    {/* COLUMNA IZQUIERDA: MÓDULOS DE OPERACIÓN (3/4 PANTALLA) */}
                    <div className="lg:col-span-8 xl:col-span-9 space-y-6">
                        
                        <div className="bg-white rounded-3xl border border-slate-200/70 shadow-sm p-8 space-y-8">
                            
                            <div className="flex items-center justify-between border-b border-slate-100 pb-5">
                                <div>
                                    <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                                        ¿Qué necesitás gestionar hoy?
                                    </h2>
                                    <p className="text-xs text-slate-400 font-medium mt-1">
                                        Selecciona una funcionalidad para iniciar el flujo operativo
                                    </p>
                                </div>
                            </div>

                            {/* LISTADO DE SECCIONES CON ACORDEÓN */}
                            <div className="space-y-6">
                                {filteredSections.map((section) => {
                                    const isCollapsed = collapsedSections[section.id];

                                    return (
                                        <div 
                                            key={section.id} 
                                            className="bg-slate-50/60 rounded-2xl border border-slate-200/60 overflow-hidden transition-all duration-200"
                                        >
                                            <div 
                                                onClick={() => toggleSection(section.id)}
                                                className="p-4 px-6 flex items-center justify-between cursor-pointer hover:bg-slate-100/70 transition-colors select-none"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold transition-transform duration-200 ${
                                                        isCollapsed ? '-rotate-90 bg-slate-200 text-slate-600' : 'bg-slate-900 text-white shadow-xs'
                                                    }`}>
                                                        ▼
                                                    </span>
                                                    <div>
                                                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-wide">
                                                            {section.header}
                                                        </h3>
                                                        <p className="text-[11px] text-slate-400 font-medium">
                                                            {section.sub}
                                                        </p>
                                                    </div>
                                                </div>

                                                <span className={`text-[10px] font-black px-3 py-1 rounded-full border uppercase shadow-2xs ${section.badgeColor}`}>
                                                    {section.modules.length} Módulos
                                                </span>
                                            </div>

                                            {/* TARJETAS DE MÓDULOS */}
                                            {!isCollapsed && (
                                                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 bg-white border-t border-slate-100 animate-fade-in">
                                                    {section.modules.map((item, idx) => {
                                                        
                                                        // ◄◄◄ RENDERIZADO CONDICIONAL DE MÓDULOS INACTIVOS (COBRANZA ART) ◄◄◄
                                                        if (item.isDisabled) {
                                                            return (
                                                                <div
                                                                    key={idx}
                                                                    className="bg-slate-50/50 border border-slate-200/60 rounded-2xl p-5 flex flex-col justify-between h-full opacity-70 cursor-not-allowed select-none relative overflow-hidden"
                                                                >
                                                                    <div>
                                                                        <div className="flex items-center justify-between mb-4">
                                                                            <div className="p-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-400">
                                                                                {item.icon}
                                                                            </div>
                                                                            <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-md border ${item.chipBg}`}>
                                                                                {item.category}
                                                                            </span>
                                                                        </div>

                                                                        <h4 className="text-base font-bold text-slate-600 tracking-tight">
                                                                            {item.title}
                                                                        </h4>

                                                                        <p className="text-slate-400 text-xs leading-relaxed font-medium mt-1.5">
                                                                            {item.description}
                                                                        </p>
                                                                    </div>

                                                                    <div className="mt-5 pt-3 border-t border-slate-200/60 flex items-center justify-between text-xs font-bold text-slate-400">
                                                                        <span className="text-[10px] uppercase tracking-wider font-extrabold">En Desarrollo</span>
                                                                        <span className="text-xs">🔒</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <NavLink
                                                                key={idx}
                                                                to={item.path}
                                                                className={`group bg-white border border-slate-200/80 ${item.hoverBorder} rounded-2xl p-5 transition-all duration-200 flex flex-col justify-between h-full shadow-2xs hover:shadow-xl hover:-translate-y-1`}
                                                            >
                                                                <div>
                                                                    <div className="flex items-center justify-between mb-4">
                                                                        <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 group-hover:bg-white transition-colors">
                                                                            {item.icon}
                                                                        </div>
                                                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-md border ${item.chipBg}`}>
                                                                            {item.category}
                                                                        </span>
                                                                    </div>

                                                                    <h4 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors tracking-tight">
                                                                        {item.title}
                                                                    </h4>

                                                                    <p className="text-slate-400 text-xs leading-relaxed font-medium mt-1.5">
                                                                        {item.description}
                                                                    </p>
                                                                </div>

                                                                <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-slate-400 group-hover:text-blue-600 transition-colors">
                                                                    <span className="text-[11px] uppercase tracking-wider font-extrabold">Ingresar</span>
                                                                    <span className="transform group-hover:translate-x-1.5 transition-transform text-sm">→</span>
                                                                </div>
                                                            </NavLink>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                        </div>
                                    );
                                })}
                            </div>

                        </div>

                    </div>

                    {/* COLUMNA DERECHA: PANEL LATERAL DE WIDGETS (1/4 PANTALLA) */}
                    <div className="lg:col-span-4 xl:col-span-3 space-y-6">
                        
                        <div className="space-y-3">
                            <div className="flex items-center justify-between px-1">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                                    Panel de Seguimiento
                                </h3>
                            </div>

                            {/* WIDGETS INTEGRADOS EN COLUMNA */}
                            {isFullUser && (
                                <div className="space-y-4">
                                    <WidgetProduccionMensual />
                                    <WidgetObjetivosSancor />
                                </div>
                            )}
                        </div>

                    </div>

                </div>
            </div>

            {/* FOOTER */}
            <footer className="mt-16 pt-6 border-t border-slate-200/80 text-center select-none">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    &copy; {new Date().getFullYear()} IB BROKERS SYSTEM &bull; INTELLIGENT INSURANCE MANAGEMENT &bull; v2.5
                </p>
            </footer>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default Cpanel;